import React, { useState, useEffect } from 'react';
import { clientsAPI, jobsAPI, financialAPI, usersAPI, subcontractorInvoicesAPI, timesheetsAPI, clientInvoicesAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { Loader2, TrendingUp, TrendingDown, DollarSign, Percent, Clock, Wallet, Download, Users } from 'lucide-react';

// Helper to get current financial year (Australian FY: July 1 - June 30)
const getCurrentFY = () => {
  const now = new Date();
  const month = now.getMonth(); // 0-11
  const year = now.getFullYear();
  // If July or later, we're in FY year/year+1, otherwise FY year-1/year
  return month >= 6 ? year : year - 1;
};

// Generate FY options (e.g., "FY24/25", "FY25/26")
const generateFYOptions = () => {
  const currentFY = getCurrentFY();
  const options = [];
  for (let i = currentFY + 1; i >= currentFY - 4; i--) {
    const startYear = i.toString().slice(-2);
    const endYear = (i + 1).toString().slice(-2);
    options.push({
      value: i,
      label: `FY${startYear}/${endYear}`,
      startDate: `${i}-07-01`,
      endDate: `${i + 1}-06-30`
    });
  }
  return options;
};

const FinancialPage = () => {
  const [loading, setLoading] = useState(true);
  const [selectedFY, setSelectedFY] = useState(getCurrentFY());
  const [monthlySummaries, setMonthlySummaries] = useState([]);
  const [clientBreakdown, setClientBreakdown] = useState([]);
  const [jobBreakdown, setJobBreakdown] = useState([]);
  const [resourceBreakdown, setResourceBreakdown] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedView, setSelectedView] = useState('monthly');
  const [yetToBeCharged, setYetToBeCharged] = useState(0);
  const [yetToBePaid, setYetToBePaid] = useState(0);
  
  const fyOptions = generateFYOptions();

  useEffect(() => {
    fetchData();
  }, [selectedFY, selectedClient]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get the date range for the selected financial year
      const fyOption = fyOptions.find(fy => fy.value === selectedFY);
      const startDate = fyOption?.startDate || `${selectedFY}-07-01`;
      const endDate = fyOption?.endDate || `${selectedFY + 1}-06-30`;
      
      const [monthlyRes, clientsRes, yetToBeChargedRes, yetToBePaidRes] = await Promise.all([
        financialAPI.getMonthly(selectedFY, selectedClient || undefined),
        clientsAPI.getAll(),
        financialAPI.getYetToBeCharged(selectedClient || undefined, startDate, endDate),
        financialAPI.getYetToBePaid(selectedClient || undefined, startDate, endDate)
      ]);
      
      setMonthlySummaries(monthlyRes.data || []);
      setClients(clientsRes.data || []);
      setYetToBeCharged(yetToBeChargedRes.data?.total || 0);
      setYetToBePaid(yetToBePaidRes.data?.total || 0);

      // Get breakdowns for the financial year
      const [clientBRes, jobBRes] = await Promise.all([
        financialAPI.getByClient(startDate, endDate),
        financialAPI.getByJob(startDate, endDate)
      ]);
      
      setClientBreakdown(clientBRes.data || []);
      setJobBreakdown(jobBRes.data || []);
      
      // Fetch resource breakdown data (aggregate from subcontractor invoices and timesheets)
      await fetchResourceBreakdown(startDate, endDate);
    } catch (error) {
      console.error('Failed to fetch financial data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch and calculate resource breakdown from existing data
  const fetchResourceBreakdown = async (startDate, endDate) => {
    try {
      const [usersRes, subInvoicesRes, timesheetsRes, clientInvoicesRes, jobsRes] = await Promise.all([
        usersAPI.getAll(),
        subcontractorInvoicesAPI.getAll(),
        timesheetsAPI.getAll(),
        clientInvoicesAPI.getAll(),
        jobsAPI.getAll()
      ]);
      
      const users = usersRes.data || [];
      const subInvoices = subInvoicesRes.data || [];
      const timesheets = timesheetsRes.data || [];
      const clientInvoices = clientInvoicesRes.data || [];
      const jobs = jobsRes.data || [];
      
      // Create job lookup map
      const jobMap = {};
      jobs.forEach(j => { jobMap[j.id] = j; });
      
      // Filter subcontractors only
      const subcontractors = users.filter(u => u.role === 'subcontractor');
      
      // Build a map of user_id -> aggregated financial data
      const resourceMap = {};
      
      // Initialize all subcontractors
      subcontractors.forEach(sub => {
        resourceMap[sub.id] = {
          resource_id: sub.id,
          resource_name: sub.name || 'Unknown',
          charged_amount: 0,
          yet_to_be_charged: 0,
          paid_amount: 0,
          yet_to_be_paid: 0,
          total_days: 0,
          total_hours: 0
        };
      });
      
      // Build a set of timesheets that have been PAID (linked to paid subcontractor invoices)
      const paidTimesheetIds = new Set();
      subInvoices.forEach(invoice => {
        if (invoice.status === 'paid') {
          (invoice.timesheet_ids || []).forEach(tsId => paidTimesheetIds.add(tsId));
        }
      });
      
      // Aggregate paid amounts from PAID subcontractor invoices
      subInvoices.forEach(invoice => {
        const userId = invoice.user_id;
        if (!userId || !resourceMap[userId]) return;
        
        // Check if invoice is within date range (by invoice_date or period)
        const invoiceDate = invoice.invoice_date || invoice.created_at?.split('T')[0];
        if (invoiceDate && invoiceDate >= startDate && invoiceDate <= endDate) {
          if (invoice.status === 'paid') {
            resourceMap[userId].paid_amount += invoice.subtotal || 0;
          }
          // Note: yet_to_be_paid is calculated from timesheets below, not from invoices
        }
      });
      
      // Build a set of all invoiced timesheet IDs and calculate charged per timesheet
      // We need to calculate proportional shares based on estimated billing values
      const invoicedTimesheets = new Map(); // tsId -> { charged: amount }
      
      // First, calculate estimated billing for each timesheet to determine proportions
      const timesheetBillingMap = new Map(); // tsId -> estimatedBilling
      timesheets.forEach(ts => {
        if (ts.status !== 'approved') return;
        const job = jobMap[ts.job_id];
        if (job) {
          const dailyRate = job.bill_rate || job.daily_rate || 0;
          const hourlyRate = job.hourly_rate || 0;
          const accomRate = job.accommodation_rate || 0;
          const kmRate = job.km_rate || 0;
          const returnTripRate = job.return_trip_rate || 0;
          
          let estimatedBilling = ((ts.total_days || 0) * dailyRate) + ((ts.total_hours || 0) * hourlyRate);
          
          // Add accommodation, km, and return trip charges from entries
          (ts.entries || []).forEach(entry => {
            estimatedBilling += (entry.accommodation_amount || 0) * accomRate;
            estimatedBilling += (entry.kms_travelled || entry.kilometres || 0) * kmRate;
            if (entry.one_way_trip && returnTripRate > 0) {
              estimatedBilling += returnTripRate;
            }
          });
          
          timesheetBillingMap.set(ts.id, estimatedBilling);
        }
      });
      
      clientInvoices.forEach(inv => {
        const tsIds = inv.timesheet_ids || [];
        if (tsIds.length === 0) return;
        
        // Include draft, sent, and paid invoices as "charged" since an invoice has been created
        if (inv.status === 'paid' || inv.status === 'sent' || inv.status === 'draft') {
          // Calculate total estimated billing for all timesheets in this invoice
          let totalEstimatedBilling = 0;
          tsIds.forEach(tsId => {
            totalEstimatedBilling += timesheetBillingMap.get(tsId) || 0;
          });
          
          // Distribute invoice amount proportionally based on each timesheet's contribution
          tsIds.forEach(tsId => {
            const tsBilling = timesheetBillingMap.get(tsId) || 0;
            let tsCharged = 0;
            
            if (totalEstimatedBilling > 0) {
              // Proportional share based on estimated billing
              tsCharged = (inv.subtotal || 0) * (tsBilling / totalEstimatedBilling);
            } else if (tsIds.length > 0) {
              // Fallback to even distribution if no billing estimates
              tsCharged = (inv.subtotal || 0) / tsIds.length;
            }
            
            if (!invoicedTimesheets.has(tsId)) {
              invoicedTimesheets.set(tsId, { charged: 0 });
            }
            invoicedTimesheets.get(tsId).charged += tsCharged;
          });
        }
      });
      
      // Aggregate timesheet data and calculate charged amounts AND yet_to_be_paid
      timesheets.forEach(ts => {
        if (ts.status !== 'approved') return;
        
        const userId = ts.user_id;
        if (!userId || !resourceMap[userId]) return;
        
        // Check if timesheet is within date range
        const periodStart = ts.period_start;
        if (periodStart && periodStart >= startDate && periodStart <= endDate) {
          resourceMap[userId].total_days += ts.total_days || 0;
          resourceMap[userId].total_hours += ts.total_hours || 0;
          
          // Calculate estimated billing based on job bill rates
          const job = jobMap[ts.job_id];
          if (job) {
            const dailyRate = job.bill_rate || job.daily_rate || 0;
            const hourlyRate = job.hourly_rate || 0;
            const estimatedBilling = ((ts.total_days || 0) * dailyRate) + ((ts.total_hours || 0) * hourlyRate);
            
            // Check if this timesheet has been invoiced
            const invoiceData = invoicedTimesheets.get(ts.id);
            if (invoiceData && invoiceData.charged > 0) {
              // Use the actual invoiced amount (distributed proportionally)
              resourceMap[userId].charged_amount += invoiceData.charged;
            } else {
              // Not invoiced yet - add estimated billing to "yet to charge"
              resourceMap[userId].yet_to_be_charged += estimatedBilling;
            }
            
            // Calculate "yet to be paid" for this timesheet if it hasn't been paid yet
            // Use the estimated pay value from the timesheet (if available) or calculate based on job pay rates
            if (!paidTimesheetIds.has(ts.id)) {
              // Calculate estimated pay using PAY rates (from job or resource)
              // Use estimated_pay if available, otherwise calculate from job rates
              const estimatedPay = ts.estimated_pay || 0;
              
              if (estimatedPay > 0) {
                resourceMap[userId].yet_to_be_paid += estimatedPay;
              } else {
                // Fallback: Calculate pay from job rates (accommodation, km, return trips not included here
                // as they require entry-level data which may not be available)
                const payDailyRate = job.daily_rate || job.pay_rate || 0;
                const payHourlyRate = job.hourly_rate || payDailyRate / 8;
                let estimatedPayCalc = ((ts.total_days || 0) * payDailyRate) + ((ts.total_hours || 0) * payHourlyRate);
                
                // Add accommodation, km, return trip from entries if available
                const accomRate = job.accommodation_rate || 0;
                const kmRate = job.km_rate || 0;
                const returnTripRate = job.return_trip_rate || 0;
                
                (ts.entries || []).forEach(entry => {
                  estimatedPayCalc += (entry.accommodation_amount || 0) * accomRate;
                  estimatedPayCalc += (entry.kms_travelled || entry.kilometres || 0) * kmRate;
                  if (entry.one_way_trip && returnTripRate > 0) {
                    estimatedPayCalc += returnTripRate;
                  }
                });
                
                resourceMap[userId].yet_to_be_paid += estimatedPayCalc;
              }
            }
          }
        }
      });
      
      // Convert map to array and calculate margins
      const breakdown = Object.values(resourceMap)
        .filter(r => r.paid_amount > 0 || r.yet_to_be_paid > 0 || r.charged_amount > 0 || r.yet_to_be_charged > 0 || r.total_days > 0 || r.total_hours > 0)
        .map(r => {
          const totalRevenue = r.charged_amount + r.yet_to_be_charged;
          const totalCost = r.paid_amount + r.yet_to_be_paid;
          const margin = totalRevenue - totalCost;
          const marginPercentage = totalRevenue > 0 ? (margin / totalRevenue) * 100 : 0;
          
          return {
            ...r,
            margin,
            margin_percentage: marginPercentage
          };
        })
        .sort((a, b) => (b.charged_amount + b.yet_to_be_charged) - (a.charged_amount + a.yet_to_be_charged));
      
      setResourceBreakdown(breakdown);
    } catch (error) {
      console.error('Failed to fetch resource breakdown:', error);
      setResourceBreakdown([]);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  // Calculate totals with new margin formula:
  // Revenue = Charged + Yet to be Charged
  // Costs = Paid + Yet to be Paid
  // Margin = Revenue - Costs
  const totals = monthlySummaries.reduce(
    (acc, m) => ({
      charged: acc.charged + (m.charged_amount || 0),
      paid: acc.paid + (m.paid_amount || 0),
    }),
    { charged: 0, paid: 0 }
  );
  
  // Ensure values are numbers (default to 0 if undefined)
  const safeYetToBeCharged = Number(yetToBeCharged) || 0;
  const safeYetToBePaid = Number(yetToBePaid) || 0;
  const safeCharged = Number(totals.charged) || 0;
  const safePaid = Number(totals.paid) || 0;
  
  // New margin calculation: (Charged + Yet to be Charged) - (Paid + Yet to be Paid)
  const totalRevenue = safeCharged + safeYetToBeCharged;
  const totalCosts = safePaid + safeYetToBePaid;
  const totalMargin = totalRevenue - totalCosts;
  const avgMarginPercent = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;
  
  // Helper function for safe number formatting
  const formatCurrency = (val) => (Number(val) || 0).toLocaleString();

  // CSV Export function
  const exportToCSV = () => {
    const fyLabel = fyOptions.find(f => f.value === selectedFY)?.label || `FY${selectedFY}`;
    let csvContent = '';
    let filename = '';

    if (selectedView === 'monthly') {
      // Monthly export
      csvContent = 'Month,Charged,Yet to Charge,Paid,Yet to Pay,Margin ($),Margin (%)\n';
      monthlySummaries.forEach(m => {
        csvContent += `"${m.period}",${m.charged_amount || 0},${m.yet_to_be_charged || 0},${m.paid_amount || 0},${m.yet_to_be_paid || 0},${m.margin || 0},${(m.margin_percentage || 0).toFixed(1)}%\n`;
      });
      // Add totals row
      csvContent += `"TOTAL",${safeCharged},${safeYetToBeCharged},${safePaid},${safeYetToBePaid},${totalMargin},${avgMarginPercent.toFixed(1)}%\n`;
      filename = `Financial_Monthly_${fyLabel}.csv`;
    } else if (selectedView === 'by-client') {
      // Client breakdown export
      csvContent = 'Client,Charged,Yet to Charge,Paid,Yet to Pay,Margin ($),Margin (%)\n';
      clientBreakdown.forEach(c => {
        csvContent += `"${c.client_name}",${c.charged_amount || 0},${c.yet_to_be_charged || 0},${c.paid_amount || 0},${c.yet_to_be_paid || 0},${c.margin || 0},${(c.margin_percentage || 0).toFixed(1)}%\n`;
      });
      filename = `Financial_ByClient_${fyLabel}.csv`;
    } else if (selectedView === 'by-job') {
      // Job breakdown export
      csvContent = 'Project,Name,Charged,Yet to Charge,Paid,Yet to Pay,Margin ($),Margin (%)\n';
      jobBreakdown.forEach(j => {
        csvContent += `"${j.job_number}","${j.job_name}",${j.charged_amount || 0},${j.yet_to_be_charged || 0},${j.paid_amount || 0},${j.yet_to_be_paid || 0},${j.margin || 0},${(j.margin_percentage || 0).toFixed(1)}%\n`;
      });
      filename = `Financial_ByProject_${fyLabel}.csv`;
    } else if (selectedView === 'by-resource') {
      // Resource breakdown export
      csvContent = 'Resource,Charged,Yet to Charge,Paid,Yet to Pay,Margin ($),Margin (%),Days,Hours\n';
      resourceBreakdown.forEach(r => {
        csvContent += `"${r.resource_name}",${r.charged_amount || 0},${r.yet_to_be_charged || 0},${r.paid_amount || 0},${r.yet_to_be_paid || 0},${(r.margin || 0).toFixed(2)},${(r.margin_percentage || 0).toFixed(1)}%,${(r.total_days || 0).toFixed(1)},${(r.total_hours || 0).toFixed(1)}\n`;
      });
      // Add totals row
      const totalCharged = resourceBreakdown.reduce((sum, r) => sum + (r.charged_amount || 0), 0);
      const totalYetToCharge = resourceBreakdown.reduce((sum, r) => sum + (r.yet_to_be_charged || 0), 0);
      const totalPaid = resourceBreakdown.reduce((sum, r) => sum + (r.paid_amount || 0), 0);
      const totalYetToPay = resourceBreakdown.reduce((sum, r) => sum + (r.yet_to_be_paid || 0), 0);
      const totalMargin = resourceBreakdown.reduce((sum, r) => sum + (r.margin || 0), 0);
      const totalRevenue = totalCharged + totalYetToCharge;
      const avgMargin = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;
      const totalDays = resourceBreakdown.reduce((sum, r) => sum + (r.total_days || 0), 0);
      const totalHours = resourceBreakdown.reduce((sum, r) => sum + (r.total_hours || 0), 0);
      csvContent += `"TOTAL",${totalCharged},${totalYetToCharge},${totalPaid},${totalYetToPay},${totalMargin.toFixed(2)},${avgMargin.toFixed(1)}%,${totalDays.toFixed(1)},${totalHours.toFixed(1)}\n`;
      filename = `Financial_ByResource_${fyLabel}.csv`;
    }

    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Financial Summary</h1>
            <p className="mt-1 text-slate-500">Margin analysis and revenue tracking</p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={exportToCSV}
              className="btn-secondary flex items-center gap-2"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="input-base w-48"
            >
              <option value="">All Clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            
            <select
              value={selectedFY}
              onChange={(e) => setSelectedFY(parseInt(e.target.value))}
              className="input-base w-32"
            >
              {fyOptions.map((fy) => (
                <option key={fy.value} value={fy.value}>{fy.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary Cards - 6 KPIs with new margin calculation */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="kpi-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Charged</p>
                <p className="mt-2 text-2xl font-bold text-slate-900">
                  ${formatCurrency(safeCharged)}
                </p>
                <p className="text-xs text-slate-500">{fyOptions.find(f => f.value === selectedFY)?.label}</p>
              </div>
              <div className="p-2 rounded-lg bg-[#194eb2]">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
          
          <div className="kpi-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Yet to be Charged</p>
                <p className="mt-2 text-2xl font-bold text-orange-600">
                  ${formatCurrency(safeYetToBeCharged)}
                </p>
                <p className="text-xs text-slate-500">Approved, not invoiced</p>
              </div>
              <div className="p-2 rounded-lg bg-orange-500">
                <Clock className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
          
          <div className="kpi-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Paid</p>
                <p className="mt-2 text-2xl font-bold text-slate-900">
                  ${formatCurrency(safePaid)}
                </p>
                <p className="text-xs text-slate-500">To subcontractors</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-500">
                <TrendingDown className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
          
          <div className="kpi-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Yet to be Paid</p>
                <p className="mt-2 text-2xl font-bold text-red-600">
                  ${formatCurrency(safeYetToBePaid)}
                </p>
                <p className="text-xs text-slate-500">Approved, not paid</p>
              </div>
              <div className="p-2 rounded-lg bg-red-500">
                <Wallet className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
          
          <div className="kpi-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Margin</p>
                <p className={`mt-2 text-2xl font-bold ${totalMargin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  ${formatCurrency(totalMargin)}
                </p>
                <p className="text-xs text-slate-500">Net profit</p>
              </div>
              <div className={`p-2 rounded-lg ${totalMargin >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}>
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
          
          <div className="kpi-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Margin %</p>
                <p className={`mt-2 text-2xl font-bold ${avgMarginPercent >= 0 ? 'text-slate-900' : 'text-red-600'}`}>
                  {(avgMarginPercent || 0).toFixed(1)}%
                </p>
                <p className="text-xs text-slate-500">Of revenue</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500">
                <Percent className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex gap-2 border-b border-slate-200">
          {['monthly', 'by-client', 'by-job', 'by-resource'].map((view) => (
            <button
              key={view}
              onClick={() => setSelectedView(view)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                selectedView === view
                  ? 'text-[#194eb2] border-[#194eb2]'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
            >
              {view === 'monthly' ? 'Monthly' : view === 'by-client' ? 'By Client' : view === 'by-job' ? 'By Project' : 'By Resource'}
            </button>
          ))}
        </div>

        {/* Monthly Table */}
        {selectedView === 'monthly' && (
          <div className="card-base overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th className="px-6 py-3 text-left">Month</th>
                    <th className="px-6 py-3 text-right">Charged</th>
                    <th className="px-6 py-3 text-right">Yet to Charge</th>
                    <th className="px-6 py-3 text-right">Paid</th>
                    <th className="px-6 py-3 text-right">Yet to Pay</th>
                    <th className="px-6 py-3 text-right">Margin ($)</th>
                    <th className="px-6 py-3 text-right">Margin (%)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {monthlySummaries.map((summary, idx) => (
                    <tr key={idx} className="table-row">
                      <td className="px-6 py-4 font-medium text-slate-900">{summary.period}</td>
                      <td className="px-6 py-4 text-right font-mono text-slate-600">
                        ${formatCurrency(summary.charged_amount)}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-orange-600">
                        ${formatCurrency(summary.yet_to_be_charged || 0)}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-slate-600">
                        ${formatCurrency(summary.paid_amount)}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-red-600">
                        ${formatCurrency(summary.yet_to_be_paid || 0)}
                      </td>
                      <td className={`px-6 py-4 text-right font-mono font-medium ${(summary.margin || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        ${formatCurrency(summary.margin)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className={`font-mono font-medium ${
                          (summary.margin_percentage || 0) >= 20 ? 'text-emerald-600' :
                          (summary.margin_percentage || 0) >= 10 ? 'text-amber-600' :
                          'text-red-600'
                        }`}>
                          {(summary.margin_percentage || 0).toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr className="font-semibold">
                    <td className="px-6 py-4 text-slate-900">Total</td>
                    <td className="px-6 py-4 text-right font-mono">${formatCurrency(safeCharged)}</td>
                    <td className="px-6 py-4 text-right font-mono text-orange-600">${formatCurrency(safeYetToBeCharged)}</td>
                    <td className="px-6 py-4 text-right font-mono">${formatCurrency(safePaid)}</td>
                    <td className="px-6 py-4 text-right font-mono text-red-600">${formatCurrency(safeYetToBePaid)}</td>
                    <td className={`px-6 py-4 text-right font-mono ${totalMargin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>${formatCurrency(totalMargin)}</td>
                    <td className={`px-6 py-4 text-right font-mono ${avgMarginPercent >= 0 ? '' : 'text-red-600'}`}>{(avgMarginPercent || 0).toFixed(1)}%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* By Client Table */}
        {selectedView === 'by-client' && (
          <div className="card-base overflow-hidden">
            <div className="card-header">
              <h3 className="font-semibold text-slate-900">Financial Summary by Client</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th className="px-6 py-3 text-left">Client</th>
                    <th className="px-6 py-3 text-right">Charged</th>
                    <th className="px-6 py-3 text-right">Yet to Charge</th>
                    <th className="px-6 py-3 text-right">Paid</th>
                    <th className="px-6 py-3 text-right">Yet to Pay</th>
                    <th className="px-6 py-3 text-right">Margin ($)</th>
                    <th className="px-6 py-3 text-right">Margin (%)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {clientBreakdown.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-8 text-center text-slate-500">
                        No data for the selected period
                      </td>
                    </tr>
                  ) : (
                    clientBreakdown.map((item, idx) => (
                      <tr key={idx} className="table-row">
                        <td className="px-6 py-4 font-medium text-slate-900">{item.client_name}</td>
                        <td className="px-6 py-4 text-right font-mono text-slate-600">
                          ${formatCurrency(item.charged_amount)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-orange-600">
                          ${formatCurrency(item.yet_to_be_charged || 0)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-slate-600">
                          ${formatCurrency(item.paid_amount)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-red-600">
                          ${formatCurrency(item.yet_to_be_paid || 0)}
                        </td>
                        <td className={`px-6 py-4 text-right font-mono font-medium ${(item.margin || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          ${formatCurrency(item.margin)}
                        </td>
                        <td className={`px-6 py-4 text-right font-mono font-medium ${(item.margin_percentage || 0) >= 0 ? '' : 'text-red-600'}`}>
                          {(item.margin_percentage || 0).toFixed(1)}%
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* By Project Table */}
        {selectedView === 'by-job' && (
          <div className="card-base overflow-hidden">
            <div className="card-header">
              <h3 className="font-semibold text-slate-900">Financial Summary by Project</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th className="px-6 py-3 text-left">Project</th>
                    <th className="px-6 py-3 text-left">Name</th>
                    <th className="px-6 py-3 text-right">Charged</th>
                    <th className="px-6 py-3 text-right">Yet to Charge</th>
                    <th className="px-6 py-3 text-right">Paid</th>
                    <th className="px-6 py-3 text-right">Yet to Pay</th>
                    <th className="px-6 py-3 text-right">Margin ($)</th>
                    <th className="px-6 py-3 text-right">Margin (%)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {jobBreakdown.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                        No data for the selected period
                      </td>
                    </tr>
                  ) : (
                    jobBreakdown.map((item, idx) => (
                      <tr key={idx} className="table-row">
                        <td className="px-6 py-4 font-mono text-sm text-slate-900">{item.job_number}</td>
                        <td className="px-6 py-4 font-medium text-slate-900">{item.job_name}</td>
                        <td className="px-6 py-4 text-right font-mono text-slate-600">
                          ${formatCurrency(item.charged_amount)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-orange-600">
                          ${formatCurrency(item.yet_to_be_charged || 0)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-slate-600">
                          ${formatCurrency(item.paid_amount)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-red-600">
                          ${formatCurrency(item.yet_to_be_paid || 0)}
                        </td>
                        <td className={`px-6 py-4 text-right font-mono font-medium ${(item.margin || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          ${formatCurrency(item.margin)}
                        </td>
                        <td className={`px-6 py-4 text-right font-mono font-medium ${(item.margin_percentage || 0) >= 0 ? '' : 'text-red-600'}`}>
                          {(item.margin_percentage || 0).toFixed(1)}%
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* By Resource Table */}
        {selectedView === 'by-resource' && (
          <div className="card-base overflow-hidden">
            <div className="card-header">
              <h3 className="font-semibold text-slate-900">Financial Summary by Resource</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th className="px-6 py-3 text-left">Resource</th>
                    <th className="px-6 py-3 text-right">Charged</th>
                    <th className="px-6 py-3 text-right">Yet to Charge</th>
                    <th className="px-6 py-3 text-right">Paid</th>
                    <th className="px-6 py-3 text-right">Yet to Pay</th>
                    <th className="px-6 py-3 text-right">Margin ($)</th>
                    <th className="px-6 py-3 text-right">Margin (%)</th>
                    <th className="px-6 py-3 text-right">Days</th>
                    <th className="px-6 py-3 text-right">Hours</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {resourceBreakdown.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-8 text-center text-slate-500">
                        No data for the selected period
                      </td>
                    </tr>
                  ) : (
                    resourceBreakdown.map((item, idx) => (
                      <tr key={idx} className="table-row">
                        <td className="px-6 py-4 font-medium text-slate-900">{item.resource_name}</td>
                        <td className="px-6 py-4 text-right font-mono text-slate-600">
                          ${formatCurrency(item.charged_amount)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-orange-600">
                          ${formatCurrency(item.yet_to_be_charged || 0)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-slate-600">
                          ${formatCurrency(item.paid_amount)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-red-600">
                          ${formatCurrency(item.yet_to_be_paid || 0)}
                        </td>
                        <td className={`px-6 py-4 text-right font-mono font-medium ${(item.margin || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          ${formatCurrency(item.margin)}
                        </td>
                        <td className={`px-6 py-4 text-right font-mono font-medium ${(item.margin_percentage || 0) >= 0 ? '' : 'text-red-600'}`}>
                          {(item.margin_percentage || 0).toFixed(1)}%
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-slate-600">
                          {(item.total_days || 0).toFixed(1)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-slate-600">
                          {(item.total_hours || 0).toFixed(1)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {resourceBreakdown.length > 0 && (
                  <tfoot className="bg-slate-50">
                    <tr className="font-semibold">
                      <td className="px-6 py-4 text-slate-900">Total</td>
                      <td className="px-6 py-4 text-right font-mono">
                        ${formatCurrency(resourceBreakdown.reduce((sum, r) => sum + (r.charged_amount || 0), 0))}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-orange-600">
                        ${formatCurrency(resourceBreakdown.reduce((sum, r) => sum + (r.yet_to_be_charged || 0), 0))}
                      </td>
                      <td className="px-6 py-4 text-right font-mono">
                        ${formatCurrency(resourceBreakdown.reduce((sum, r) => sum + (r.paid_amount || 0), 0))}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-red-600">
                        ${formatCurrency(resourceBreakdown.reduce((sum, r) => sum + (r.yet_to_be_paid || 0), 0))}
                      </td>
                      <td className={`px-6 py-4 text-right font-mono ${
                        resourceBreakdown.reduce((sum, r) => sum + (r.margin || 0), 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                        ${formatCurrency(resourceBreakdown.reduce((sum, r) => sum + (r.margin || 0), 0))}
                      </td>
                      <td className="px-6 py-4 text-right font-mono">
                        {(() => {
                          const totalRev = resourceBreakdown.reduce((sum, r) => sum + (r.charged_amount || 0) + (r.yet_to_be_charged || 0), 0);
                          const totalMargin = resourceBreakdown.reduce((sum, r) => sum + (r.margin || 0), 0);
                          return totalRev > 0 ? ((totalMargin / totalRev) * 100).toFixed(1) : '0.0';
                        })()}%
                      </td>
                      <td className="px-6 py-4 text-right font-mono">
                        {resourceBreakdown.reduce((sum, r) => sum + (r.total_days || 0), 0).toFixed(1)}
                      </td>
                      <td className="px-6 py-4 text-right font-mono">
                        {resourceBreakdown.reduce((sum, r) => sum + (r.total_hours || 0), 0).toFixed(1)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FinancialPage;
